import { useQuery } from '@tanstack/react-query'
import { partition } from 'lodash'
import { useMemo } from 'react'

import { RecordFactory } from '../models/RecordFactory'
import { Subaccount } from '../models/Subaccount'
import { User } from '../models/User'
import { useDebounced } from './useDebounced'

type UserSearchType = Parameters<ReturnType<typeof RecordFactory.getInstance>['searchUser']>[1]

export const useSearchableClients = ({
    searchValue,
    isEnabled = true,
    type = 'user',
}: {
    searchValue: string
    isEnabled?: boolean
    type?: UserSearchType
}): { clients: User[]; subaccounts: Subaccount[]; isLoading: boolean } => {
    const queryKey = useMemo(() => ['search-clients', searchValue, type], [searchValue, type])
    const debouncedQueryKey = useDebounced(queryKey, 500)

    const { data, isLoading } = useQuery({
        queryKey: debouncedQueryKey,
        queryFn: () => searchClients(searchValue, type),
        cacheTime: 1000 * 5,
        enabled: isEnabled,
    })

    if (data === undefined) {
        return { clients: [], subaccounts: [], isLoading }
    }

    return { clients: data.users, subaccounts: data.subaccounts, isLoading }
}

const searchClients = async (
    searchText: string,
    type: UserSearchType
): Promise<{ users: User[]; subaccounts: Subaccount[] }> => {
    if (searchText === '') {
        const result = await RecordFactory.getInstance().fetchClients(searchText, undefined)
        if (!result.ok) {
            throw result.error
        }
        return { users: result.value.users ?? [], subaccounts: result.value.subaccounts ?? [] }
    }
    else {
        const result = await RecordFactory.getInstance().searchUser(searchText, type)
        if (!result.ok) {
            throw result.error
        }
        const [users, subaccounts] = partition(result.value, (user) => user instanceof User)
        return { users, subaccounts }
    }
}
